import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, ArrowLeft, CheckCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.5.781 — Same anti-flash pattern as LoginPage: resolve whitelabel branding
// synchronously from localStorage on first paint so reseller domains never
// briefly show Konnect green/logo before their own brand loads.
const useHostBrand = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const cacheKey = `__wl_brand:${host}`;
  const [brand, setBrand] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - (parsed.__cachedAt || 0) < 86400000) return parsed.brand;
    } catch { /* ignore */ }
    return null;
  });
  const [resolved, setResolved] = useState(() => !!brand);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/whitelabel/public/by-host`, { params: { host } })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.matched) {
          setBrand(r.data.brand);
          try { localStorage.setItem(cacheKey, JSON.stringify({ brand: r.data.brand, __cachedAt: Date.now() })); } catch { /* ignore */ }
        } else {
          try { localStorage.removeItem(cacheKey); } catch { /* ignore */ }
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, []);
  return { brand, resolved };
};

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { forgotPassword } = useAuth();
  const { brand, resolved: brandResolved } = useHostBrand();

  // Derived branding values (fall back to Konnect defaults)
  const logo = brand?.logo_url || '/konnect-logo.png';
  const brandName = brand?.company_name || 'Konnect';
  const primary = brand?.primary_color || null;
  const secondary = brand?.secondary_color || null;
  const btnStyle = primary ? { backgroundColor: primary, borderColor: primary } : undefined;
  const bgStyle = secondary
    ? { background: `linear-gradient(135deg, ${secondary} 0%, ${secondary}f0 100%)` }
    : undefined;

  // Anti-flash guard for whitelabel hosts
  const _host = typeof window !== 'undefined' ? window.location.hostname : '';
  const _looksWhitelabel = !!_host && !/^(localhost|127\.|konnect\.kaharian\.com|.*\.preview\.emergentagent\.com)$/i.test(_host);
  if (_looksWhitelabel && !brandResolved && !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white" data-testid="forgot-brand-loading">
        <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await forgotPassword(email);
    if (result.success) setSuccess(true);
    else setError(result.error);
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={bgStyle} data-testid="forgot-success-page">
        <Card className="w-full max-w-md shadow-lg bg-white" data-testid="forgot-password-success">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                 style={primary ? { backgroundColor: `${primary}22` } : undefined}>
              <CheckCircle className="h-8 w-8" style={primary ? { color: primary } : undefined} />
            </div>
            <h2 className="text-xl font-heading font-semibold mb-2">Check your email</h2>
            <p className="text-gray-medium mb-6">
              If an account exists for {email}, you&apos;ll receive a password reset link shortly.
            </p>
            <Link to="/login">
              <Button className="w-full text-white" style={btnStyle} data-testid="forgot-back-to-signin-btn">
                Back to Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={bgStyle} data-testid="forgot-password-page">
      <Card className="w-full max-w-md shadow-lg bg-white" data-testid="forgot-password-form">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
            <img src={logo} alt={brandName} className="h-10 w-auto object-contain" />
          </Link>
          <CardTitle className="text-2xl font-heading">Forgot password?</CardTitle>
          <CardDescription>Enter your email and we&apos;ll send you a reset link</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
                className="focus-ring"
              />
            </div>
            <Button
              type="submit"
              className="w-full text-white"
              style={btnStyle}
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>
          </form>
          <Link
            to="/login"
            className="flex items-center justify-center gap-2 mt-6 text-sm text-gray-medium hover:text-text-primary"
            data-testid="forgot-back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Sign In
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
